import { AccountInfo, AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser';
import { createContext } from 'react';
import { GroupItem } from '@lib/definitions';
import { isTokenNearlyExpired } from '@utils/jwt';
import { getTokenAccess } from '@utils/callApiService';
import { IContractServiceProperties } from '@definitions/index';

export const TOKEN_SESSION_CHECK_INTERVAL = 60000; // Check every 60 seconds
export const TOKEN_EXPIRY_SESSION_BUFFER = 180000; // 180 seconds before actual expiration
export const TOKEN_EXPIRY_BUFFER = ((TOKEN_SESSION_CHECK_INTERVAL + TOKEN_EXPIRY_SESSION_BUFFER) / 1000) + 60; // x seconds before actual expiration

class AuthenticatedContext {
    instance: IPublicClientApplication;
    apiTokenResult: AuthenticationResult;
    activeAccount: AccountInfo;
    userGroups: GroupItem[];
    isAdminRole: boolean;
    isAADTenant: boolean;
    isAADAdminRole: boolean;
    unsavedChange?: boolean;
    callbackUnSavedChange?: Function;
    callbackRedirect?: Function;
    clientServices?: IContractServiceProperties;

    setApiTokenResult?: Function;

    constructor(
        instance: IPublicClientApplication,
        apiTokenResult: AuthenticationResult,
        activeAccount: AccountInfo,
        userGroups: GroupItem[],
        isAdminRole: boolean,
        isAADTenant: boolean,
        isAADAdminRole: boolean,
        clientServices?: IContractServiceProperties,
        setApiTokenResult?: Function,
    ) {
        this.instance = instance;
        this.apiTokenResult = apiTokenResult;
        this.activeAccount = activeAccount;
        this.userGroups = userGroups;
        this.isAdminRole = isAdminRole;
        this.isAADTenant = isAADTenant;
        this.isAADAdminRole = isAADAdminRole;
        this.clientServices = clientServices;
        this.setApiTokenResult = setApiTokenResult;
    }

    static create(data: any): AuthenticatedContext {
        const context = new AuthenticatedContext(
            data.instance,
            data.apiTokenResult,
            data.activeAccount,
            data.userGroups,
            data.isAdminRole,
            data.isAADTenant,
            data.isAADAdminRole,
            data.clientServices,
            data.setApiTokenResult,
        );

        if (data.unsavedChange) {
            context.unsavedChange = data.unsavedChange;
        }
        if (data.callbackUnSavedChange) {
            context.callbackUnSavedChange = data.callbackUnSavedChange;
        }
        if (data.callbackRedirect) {
            context.callbackRedirect = data.callbackRedirect;
        }
        return context;
    }

    refreshToken() {
        const accessToken = this.apiTokenResult?.accessToken;
        if (accessToken) {
            if (isTokenNearlyExpired(accessToken)) {
                //Get new token if nearly expired
                getTokenAccess(this.instance, this.activeAccount, true, true).then((res) => {
                    if (res) {
                        this.apiTokenResult = res;//May not work by directly setting?
                        if (this.setApiTokenResult && res.accessToken && res.accessToken != this.apiTokenResult?.accessToken) {
                            //Callback to update the authentication token if access token changed
                            this.setApiTokenResult(res);
                        }
                    }
                });
            }
        };
        return this.apiTokenResult;
    }

    tryLogout() {
        if (this.instance) {
            try {
                this.instance.clearCache();
                this.instance.logoutRedirect({
                    account: this.instance.getActiveAccount(),
                    onRedirectNavigate: () => false,
                });
            } catch { };
        }
    }

    setUnSavedChange(unsavedChange: boolean): void {
        this.unsavedChange = unsavedChange;
        this.callbackUnSavedChange = undefined;
    }

    setCallbackUnSavedChange(callback: Function): void {
        this.callbackUnSavedChange = callback;
    }

    setCallbackRedirect(callback: Function | undefined): void {
        this.callbackRedirect = callback;
    }
}

export { AuthenticatedContext }
export const AuthorizationContext = createContext({} as AuthenticatedContext);

